import { Target } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { N8NPick } from "@/hooks/useAnalysis";
import { OddsRow, resolveOddsForPick } from "@/lib/oddsEstimator";
import { SG7TacticalPanel, HeroPickCard, MiniPickCard } from "./sg7";

// Expanded pick types
export type PickType =
  | "win_home"
  | "win_away"
  | "draw"
  | "goals_over"
  | "goals_under"
  | "btts_yes"
  | "btts_no"
  | "double_chance"
  | "combo"
  | "ht_ft"
  | "no_bet"
  | "team_total"
  | "h1_goal"
  | "h2_goal"
  | "h1_total"
  | "h2_total"
  | "other";

export interface TechnicalPick {
  label: string;
  odds?: number;
  justification?: string;
  confidence?: "high" | "medium" | "low";
  type: PickType;
  isCombo?: boolean;
  noBet?: boolean;
  pickId?: string;
}

interface TechnicalPicksCardProps {
  picks?: TechnicalPick[];
  mdArticle?: string;  // Markdown to extract additional picks not in structured array
  oddsData?: OddsRow | null;
  homeTeamName?: string;
  awayTeamName?: string;
  className?: string;
}

// Convert N8N picks format to TechnicalPick format
export function convertN8NPicks(n8nPicks: N8NPick[]): TechnicalPick[] {
  if (!n8nPicks || !Array.isArray(n8nPicks)) return [];

  return n8nPicks
    .filter((pick) => pick && pick.label) // Filter invalid picks
    .map((pick) => {
      const type = mapMarketToPickType(pick.market, pick.selection, pick.label);
      const isCombo = ["DC_TOTAL", "RESULT_TOTAL", "HT_FT"].includes(pick.market) || pick.label.includes("+");

      return {
        label: pick.label,
        odds: pick.odd,
        pickId: pick.id,
        justification: pick.rationale || "",
        confidence: pick.tier === "safe" ? ("high" as const) : ("medium" as const),
        type: pick.no_bet ? "no_bet" : isCombo ? "combo" : type,
        isCombo,
        noBet: pick.no_bet || false,
      };
    })
    .slice(0, 12); // Max 12 picks
}

// Map N8N market to PickType
function mapMarketToPickType(market: string, selection?: string, label?: string): PickType {
  const marketUpper = market?.toUpperCase() || "";
  const selectionLower = selection?.toLowerCase() || "";
  const labelLower = label?.toLowerCase() || "";

  switch (marketUpper) {
    case "FT_1X2":
      if (selectionLower === "home" || selectionLower === "1") return "win_home";
      if (selectionLower === "away" || selectionLower === "2") return "win_away";
      if (selectionLower === "draw" || selectionLower === "x") return "draw";
      return "other";
    case "TOTAL":
      if (labelLower.includes("under") || labelLower.includes("menos")) return "goals_under";
      if (labelLower.includes("over") || labelLower.includes("mais")) return "goals_over";
      return "goals_under";
    case "BTTS":
      if (selectionLower === "yes" || labelLower.includes("sim")) return "btts_yes";
      return "btts_no";
    case "DOUBLE_CHANCE":
      return "double_chance";
    case "DC_TOTAL":
    case "RESULT_TOTAL":
      return "combo";
    case "HT_FT":
      return "ht_ft";
    // NEW: Team Total (ex: "Away Under 1.5")
    case "TEAM_TOTAL":
    case "TEAM_TOTAL_FT_OU":
      return "team_total";
    // NEW: Goal in 1st Half
    case "H1_GOAL":
    case "GOAL_1H_YN":
      return "h1_goal";
    // NEW: Goal in 2nd Half
    case "H2_GOAL":
    case "GOAL_2H_YN":
      return "h2_goal";
    // NEW: 1st Half Over/Under
    case "H1_TOTAL":
      return "h1_total";
    // NEW: 2nd Half Over/Under
    case "H2_TOTAL":
      return "h2_total";
    default:
      return "other";
  }
}

// Extract picks from the "Palpites Técnicos" section (with or without ## header)
// Uses block-based extraction to handle justifications on new lines
export function extractPicksFromMarkdown(markdown: string | null | undefined): TechnicalPick[] {
  if (!markdown) return [];

  const picks: TechnicalPick[] = [];

  // 1. Find the "Palpites Técnicos" section - accepts with or without ## header
  // Supports: "## Palpites Técnicos", "## 13) Palpites Técnicos", "### Palpites Técnicos", or just "Palpites Técnicos" at line start
  // Lookahead stops at: ---, ## header, ### header, or end of string
  const picksSection = markdown.match(
    /(?:^|\n)(?:#{1,3}\s*)?(?:\d+\)\s*)?(?:🎯\s*)?Palpites\s*Técnicos([\s\S]*?)(?=\n## \d+\)|\n---|\n#{2,3} [A-Z]|$)/im,
  );
  if (!picksSection) return [];

  const section = picksSection[0];

  // 2. Split section by pick markers with multiline flag for proper line anchoring
  // Accepts: "1. **", "- **", "* **" (numbered or bullet lists)
  const pickBlocks = section.split(/(?=^\s*(?:\d+\.\s*|[-*]\s*)\*\*)/m);

  // Debug logs for validation
  console.debug('[TechnicalPicksCard] pickBlocks count:', pickBlocks.length);

  for (const block of pickBlocks) {
    // Match header with multiline flag: "1. **Pick**", "- **Pick**", "* **Pick**"
    const headerMatch = block.match(/^\s*(?:\d+\.\s*|[-*]\s*)\*\*(.+?)\*\*/m);
    if (!headerMatch) continue;

    let rawLabel = headerMatch[1].trim();
    
    // Extract odds from inside label (e.g., "Away Win (Odd 1.62)")
    let odds: number | undefined;
    const oddsMatch = rawLabel.match(/\((?:odds?\s+)?([0-9.]+)\)\s*$/i);
    if (oddsMatch) {
      odds = parseFloat(oddsMatch[1]);
      rawLabel = rawLabel.replace(/\s*\((?:odds?\s+)?[0-9.]+\)\s*$/i, "").trim();
    }

    // Remove "Pick N:" prefix
    const finalLabel = rawLabel.replace(/^Pick\s*\d*:?\s*/i, "").trim();
    if (!finalLabel) continue;

    // Extract justification from rest of block (handles new lines)
    let justification = "";
    const restOfBlock = block.slice(headerMatch[0].length).trim();
    
    // Remove leading dashes/colons and "Justificação:" prefix
    const cleanedRest = restOfBlock.replace(/^[-–—:]+\s*/, "");
    const justMatch = cleanedRest.match(/^(?:Justifica[çc][aã]o\s*:\s*)?(.+)/is);
    if (justMatch && justMatch[1]) {
      // Get first meaningful line of justification
      const lines = justMatch[1].trim().split('\n');
      justification = lines[0]?.trim() || "";
    }

    // Also check if justification contains odds (format: "A odd de 2.1 é...")
    if (odds === undefined && justification) {
      const justOddsMatch = justification.match(/odd(?:s)?\s+(?:de\s+)?([0-9.]+)/i);
      if (justOddsMatch) {
        odds = parseFloat(justOddsMatch[1]);
      }
    }

    // Determine pick type with expanded categories
    const type = detectPickType(finalLabel);
    const isCombo =
      finalLabel.includes("+") ||
      finalLabel.toLowerCase().includes(" e ") ||
      finalLabel.toLowerCase().includes("combo");

    picks.push({
      label: finalLabel,
      odds,
      justification,
      type: isCombo ? "combo" : type,
      isCombo,
      confidence: "medium",
    });
  }

  // Debug: log total picks extracted
  console.debug('[TechnicalPicksCard] picks extracted from markdown:', picks.length);

  return picks.slice(0, 12); // Max 12 picks
}

// Detect pick type from label text
function detectPickType(label: string): PickType {
  const lower = label.toLowerCase();

  // NEW: Team Total (ex: "Away Under 1.5", "Home Team Total Under 2.5")
  // Check this BEFORE general over/under to avoid misclassification
  if ((lower.includes("home") || lower.includes("away") || lower.includes("team total") || 
       lower.includes("casa") || lower.includes("fora")) &&
      (lower.includes("under") || lower.includes("over") || lower.includes("menos") || lower.includes("mais"))) {
    return "team_total";
  }

  // NEW: Goal in 1st Half (ex: "Goal in 1st Half — Yes", "Golo 1ª Parte")
  if ((lower.includes("1st half") || lower.includes("1ª parte") || lower.match(/\b1h\b/)) &&
      (lower.includes("goal") || lower.includes("golo"))) {
    return "h1_goal";
  }

  // NEW: Goal in 2nd Half
  if ((lower.includes("2nd half") || lower.includes("2ª parte") || lower.match(/\b2h\b/)) &&
      (lower.includes("goal") || lower.includes("golo"))) {
    return "h2_goal";
  }

  // NEW: Half Over/Under (ex: "1H Over 0.5", "2H Under 1.5")
  if (lower.match(/\b1h\s*(over|under|mais|menos)/i)) {
    return "h1_total";
  }
  if (lower.match(/\b2h\s*(over|under|mais|menos)/i)) {
    return "h2_total";
  }

  // Win types
  if (lower.includes("vitória") || lower.includes("vence") || lower.includes("win")) {
    if (lower.includes("casa") || lower.includes("home") || lower.match(/^vitória\s+\w+$/)) {
      return "win_home";
    }
    if (lower.includes("fora") || lower.includes("away") || lower.includes("visitante")) {
      return "win_away";
    }
    return "win_home";
  }

  // Draw
  if (lower.includes("empate") || lower.includes("draw")) {
    return "draw";
  }

  // Goals over/under (general - after team-specific check)
  if (lower.includes("mais de") || lower.includes("over") || lower.includes("+")) {
    return "goals_over";
  }
  if (lower.includes("menos de") || lower.includes("under")) {
    return "goals_under";
  }

  // BTTS
  if (lower.includes("ambas") && lower.includes("marcam")) {
    if (lower.includes("não") || lower.includes("no")) {
      return "btts_no";
    }
    return "btts_yes";
  }
  if (lower.includes("btts")) {
    if (lower.includes("não") || lower.includes("no")) {
      return "btts_no";
    }
    return "btts_yes";
  }

  // Double chance
  if (
    lower.includes("double chance") ||
    lower.includes("dupla chance") ||
    lower.match(/1x|x2|12/) ||
    lower.includes("ou empate")
  ) {
    return "double_chance";
  }

  // Combo detection
  if (lower.includes("+") || lower.includes(" e ") || lower.includes("combo")) {
    return "combo";
  }

  // Half time / Full time
  if (lower.includes("intervalo") || lower.includes("ht/ft") || lower.includes("ht-ft")) {
    return "ht_ft";
  }

  return "other";
}

// Get odds for a pick using the estimator - supports combos
interface OddsResult {
  value: number | null;
  isEstimated: boolean;
}

function getOddsForPick(pick: TechnicalPick, oddsData?: OddsRow | null): OddsResult {
  // Skip no-bet picks
  if (pick.noBet || pick.type === "no_bet") {
    return { value: null, isEstimated: false };
  }

  // Use resolveOddsForPick if we have pickId and oddsData
  if (pick.pickId && oddsData) {
    const resolved = resolveOddsForPick(pick.pickId, pick.odds || null, oddsData);
    if (resolved.value > 1.01) {
      return {
        value: resolved.value,
        isEstimated: resolved.source === "EST",
      };
    }
  }

  // Fallback to pick.odds (from N8N/markdown)
  if (pick.odds && pick.odds > 1) {
    return { value: pick.odds, isEstimated: false };
  }

  return { value: null, isEstimated: false };
}

// Semantic canonical key for deduplication (mirrors generatePickCanonicalKey from useSG7Picks)
function getPickCanonicalKey(pick: TechnicalPick): string {
  const label = (pick.label || '').toLowerCase();
  const id = (pick.pickId || '').toLowerCase();
  const market = id.split(':')[0]?.toUpperCase() || '';

  // TEAM_TOTAL variants: "team_total:home|away:line:over|under"
  if (market.includes('TEAM_TOTAL') ||
      label.includes('home team') || label.includes('away team') ||
      id.includes('team_total') || id.includes('team_home') || id.includes('team_away') ||
      ((label.includes('home') || label.includes('away')) &&
       (label.includes('over') || label.includes('under')))) {
    const isHome = label.includes('home') || id.includes('home');
    const team = isHome ? 'home' : 'away';
    const lineMatch = label.match(/(\d+\.?\d*)/);
    const line = lineMatch ? lineMatch[1] : '0.5';
    const isOver = label.includes('over') || id.includes('over');
    return `team_total:${team}:${line}:${isOver ? 'over' : 'under'}`;
  }

  // TOTAL (Over/Under) - not team specific
  if (market === 'TOTAL' ||
      ((label.includes('over') || label.includes('under')) &&
       !label.includes('team') && !label.includes('home') && !label.includes('away'))) {
    const lineMatch = label.match(/(\d+\.?\d*)/);
    const line = lineMatch ? lineMatch[1] : '2.5';
    const isOver = label.includes('over');
    return `total:${line}:${isOver ? 'over' : 'under'}`;
  }

  // BTTS
  if (market === 'BTTS' || label.includes('btts') || label.includes('ambas')) {
    const isYes = label.includes('yes') || label.includes('sim');
    return `btts:${isYes ? 'yes' : 'no'}`;
  }

  // DOUBLE_CHANCE
  if (market === 'DOUBLE_CHANCE' || market.includes('DC')) {
    const dc = label.match(/(1x|x2|12)/i);
    return `dc:${dc ? dc[1].toLowerCase() : label.replace(/[^a-z0-9]/g, '')}`;
  }

  // GOAL_1H / GOAL_2H
  if (market === 'GOAL_1H_YN' || market === 'H1_GOAL' || (label.includes('1st half') && label.includes('goal'))) {
    const isYes = label.includes('yes') || label.includes('sim');
    return `goal_1h:${isYes ? 'yes' : 'no'}`;
  }
  if (market === 'GOAL_2H_YN' || market === 'H2_GOAL' || (label.includes('2nd half') && label.includes('goal'))) {
    const isYes = label.includes('yes') || label.includes('sim');
    return `goal_2h:${isYes ? 'yes' : 'no'}`;
  }

  // FT_1X2
  if (market === 'FT_1X2' || market === '1X2') {
    if (label.includes('home') || label.includes('1')) return 'ft_1x2:home';
    if (label.includes('away') || label.includes('2')) return 'ft_1x2:away';
    if (label.includes('draw') || label.includes('x')) return 'ft_1x2:draw';
    return `ft_1x2:${label.replace(/[^a-z0-9]/g, '')}`;
  }

  // EUROPEAN_HANDICAP
  if (market === 'EUROPEAN_HANDICAP' || market === 'EH' || market === 'HANDICAP_EU' ||
      id.includes('eh') || id.includes('handicap')) {
    const isHome = label.includes('home') || id.includes('home');
    const lineMatch = label.match(/([+-]?\d+\.?\d*)/);
    const line = lineMatch ? lineMatch[1] : '0';
    return `eh:${isHome ? 'home' : 'away'}:${line}`;
  }

  // Fallback
  return `other:${label.replace(/[^a-z0-9]/g, '')}`;
}

export function TechnicalPicksCard({
  picks,
  mdArticle,
  oddsData,
  homeTeamName,
  awayTeamName,
  className,
}: TechnicalPicksCardProps) {
  const { t } = useTranslation('analysis');
  // 1. Start with structured picks
  const structuredPicks = picks || [];
  
  // 2. Extract picks from markdown
  const markdownPicks = extractPicksFromMarkdown(mdArticle);
  
  // 3. Merge & deduplicate using semantic canonical keys
  const seenKeys = new Set<string>();
  const allPicks: TechnicalPick[] = [];
  
  // Structured picks first (priority), then markdown picks
  for (const pick of [...structuredPicks, ...markdownPicks]) {
    const key = getPickCanonicalKey(pick);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      allPicks.push(pick);
    }
  }
  
  // 4. Filter out picks without a usable odd.
  // Policy: a pick without odd cannot be published (text may be ready but the pick itself
  // is not actionable without a price). Skip no-bet picks too (they would already return null).
  const picksWithOdds = allPicks.filter(p => {
    if (p.noBet || p.type === "no_bet") return false;
    const o = getOddsForPick(p, oddsData);
    return o.value !== null && o.value > 1.01;
  });

  // Cap at 12
  const finalPicks = picksWithOdds.slice(0, 12);

  // Don't render if no picks at all
  if (finalPicks.length === 0) return null;

  // Separate hero pick (first) from mini picks (rest)
  const [heroPick, ...miniPicks] = finalPicks;

  // Get odds for hero pick
  const heroOdds = getOddsForPick(heroPick, oddsData);

  return (
    <SG7TacticalPanel className={cn("", className)}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-[hsl(var(--sg7-gold)/0.15)]">
            <Target className="h-5 w-5 text-[hsl(var(--sg7-gold))]" />
          </div>
          <h2 className="text-base font-black text-[hsl(var(--sg7-text))] tracking-tight">{t('picks.title')}</h2>
        </div>
      </div>

      {/* Picks content */}
      <div className="px-5 pb-5 space-y-4">
        {/* Hero pick - main highlighted pick */}
        {heroPick && <HeroPickCard pick={heroPick} displayOdds={heroOdds.value} isEstimated={heroOdds.isEstimated} />}

        {/* Mini picks grid */}
        {miniPicks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {miniPicks.map((pick, index) => {
              const oddsResult = getOddsForPick(pick, oddsData);
              return (
                <MiniPickCard
                  key={`${pick.label}-${index}`}
                  pick={pick}
                  displayOdds={oddsResult.value}
                  isEstimated={oddsResult.isEstimated}
                  animationDelay={index * 60}
                />
              );
            })}
          </div>
        )}

        {/* Footer disclaimer */}
        <div className="pt-4 border-t border-[hsl(var(--sg7-stroke-subtle))]">
          <p className="text-[10px] text-[hsl(var(--sg7-text-subtle))] text-center uppercase tracking-wide">
            {t('picks.disclaimer')}
          </p>
        </div>
      </div>
    </SG7TacticalPanel>
  );
}
